import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Settings2,
  Tag,
  BarChart3,
  ClipboardList,
  HelpCircle,
  Calendar,
  CheckSquare,
  FileText,
  Receipt
} from 'lucide-react';
import toast from 'react-hot-toast';

// 📝 COMMENT: Import the CustomizationCard component
import CustomizationCard from './CustomizationCard';
import InvoiceQuotationCard from './InvoiceQuotationCard';

// 📝 COMMENT: Import your actual web Redux actions and services
import { userPreferences } from '../../actions/actions';
import { putUserPreferences } from '../../services/leadService';

interface CustomizationOption {
  title: string;
  type: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const CustomizationContainer: React.FC = () => {
  const dispatch = useDispatch();
  
  // 📝 COMMENT: Use your actual web Redux selectors
  const userPrefs = useSelector(
    (state: any) => state?.rootReducers?.user?.userPreferences || {}
  );

  const userData = useSelector(
    (state: any) => state?.rootReducers?.userData?.userDetails || {}
  );

  // 📝 COMMENT: Loading state
  const [isLoading, setIsLoading] = useState(false);

  // 📝 COMMENT: Duplicate leads toggle - matches mobile isDuplicateLeadAllow
  const [isDuplicateToggle, setIsDuplicateToggle] = useState(
    userData?.userPreference?.isDuplicateLeadAllow ?? false
  );

  // 📝 COMMENT: Define 5 lead customization options
  const leadCustomizationOptions: CustomizationOption[] = useMemo(() => [
    {
      title: 'Lead Labels',
      type: 'labels',
      description: 'Create custom labels for organizing leads',
      icon: <Tag size={24} />,
      color: '#3b82f6'
    },
    {
      title: 'Lead Status',
      type: 'status',
      description: 'Customize stages in your sales pipeline',
      icon: <BarChart3 size={24} />,
      color: '#10b981'
    },
    {
      title: 'Lead Form Fields',
      type: 'customForm',
      description: 'Add custom fields to your lead forms',
      icon: <ClipboardList size={24} />,
      color: '#f59e0b'
    },
    {
      title: 'Lead Activities',
      type: 'activityType',
      description: 'Create custom activity types for lead tracking',
      icon: <Calendar size={24} />,
      color: '#8b5cf6'
    },
    {
      title: 'Task Types',
      type: 'taskType',
      description: 'Create custom task categories',
      icon: <CheckSquare size={24} />,
      color: '#14b8a6'
    }
  ], []);

  // 📝 COMMENT: Define 2 invoice/quotation customization options
  const invoiceQuotationOptions: CustomizationOption[] = useMemo(() => [
    {
      title: 'Invoice Customization',
      type: 'invoice',
      description: 'Customize invoice settings, status, and fields',
      icon: <Receipt size={24} />,
      color: '#ef4444'
    },
    {
      title: 'Quotation Customization',
      type: 'quotation',
      description: 'Customize quotation settings, status, and fields',
      icon: <FileText size={24} />,
      color: '#06b6d4'
    }
  ], []);

  // 📝 COMMENT: Combine all options for stats
  const allCustomizationOptions = useMemo(
    () => [...leadCustomizationOptions, ...invoiceQuotationOptions],
    [leadCustomizationOptions, invoiceQuotationOptions]
  );

  // 📝 COMMENT: Handle duplicate leads toggle - using web Redux pattern
  const handleDuplicateToggle = useCallback(async (newValue: boolean) => {
    try {
      setIsLoading(true);
      
      // 📝 COMMENT: Update Redux state using web action
      const updatedPrefs = {
        ...userPrefs,
        isDuplicateLeadAllow: newValue
      };
      
      dispatch(userPreferences(updatedPrefs));
      
      // 📝 COMMENT: Update backend using web service
      const response = await putUserPreferences({
        isDuplicateLeadAllow: newValue
      });

      if (response?.status) {
        setIsDuplicateToggle(newValue);
        toast.success('Duplicate leads preference updated successfully');
      } else {
        throw new Error('Failed to update preference');
      }
    } catch (error) {
      console.error('Error updating duplicate leads preference:', error);
      toast.error('Failed to update preference');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userPrefs]);

  // 📝 COMMENT: Get total items count for stats
  const totalItems = useMemo(() => {
    return leadCustomizationOptions.reduce((total, option) => {
      return total + (userPrefs[option.type]?.length || 0);
    }, 0);
  }, [userPrefs, leadCustomizationOptions]);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: '24px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header Section */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '12px' 
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              backgroundColor: '#eff6ff',
              borderRadius: '12px',
              marginRight: '16px'
            }}>
              <Settings2 size={28} className="text-blue-600" />
            </div>
            <div>
              <h1 style={{ 
                margin: '0 0 4px 0', 
                fontSize: '32px', 
                fontWeight: '800', 
                color: '#111827',
                letterSpacing: '-0.025em'
              }}>
                Customization Center
              </h1>
              <p style={{ 
                margin: 0, 
                fontSize: '16px', 
                color: '#6b7280' 
              }}>
                Customize your CRM to match your business needs
              </p>
            </div>
          </div>

          {/* Stats Bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            padding: '16px 20px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                backgroundColor: '#22c55e',
                borderRadius: '50%'
              }} />
              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                Total Customizations:
              </span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                {totalItems}
              </span>
            </div>

            <div style={{ 
              width: '1px', 
              height: '16px', 
              backgroundColor: '#e5e7eb' 
            }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {leadCustomizationOptions.map((option, index) => (
                <div key={option.type} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    backgroundColor: option.color,
                    borderRadius: '50%'
                  }} />
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    {option.title}:
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                    {userPrefs[option.type]?.length || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 320px',
          gap: '24px',
          alignItems: 'start'
        }}>
          {/* Left Column - Customization Cards */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ marginBottom: '8px' }}>
              <h2 style={{ 
                margin: '0 0 4px 0', 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#111827' 
              }}>
                Customization Options
              </h2>
              <p style={{ 
                margin: 0, 
                fontSize: '14px', 
                color: '#6b7280' 
              }}>
                Click on any card to expand and manage your customizations
              </p>
            </div>

            {/* Render Lead Customization Cards */}
            {leadCustomizationOptions.map((option) => (
              <CustomizationCard
                key={option.type}
                title={option.title}
                type={option.type}
                description={option.description}
                icon={option.icon}
                color={option.color}
              />
            ))}

            {/* Render Invoice/Quotation Customization Cards */}
            {invoiceQuotationOptions.map((option) => (
              <InvoiceQuotationCard
                key={option.type}
                type={option.type as 'invoice' | 'quotation'}
                title={option.title}
                color={option.color}
              />
            ))}
          </div>

          {/* Right Column - Settings & Info */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            position: 'sticky',
            top: '24px'
          }}>
            {/* General Settings Card */}
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                margin: '0 0 16px 0', 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Settings2 size={18} className="text-gray-600" />
                General Settings
              </h3>

              {/* Duplicate Leads Toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}>
                <div>
                  <h4 style={{ 
                    margin: '0 0 4px 0', 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#111827' 
                  }}>
                    Allow Duplicate Leads
                  </h4>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '12px', 
                    color: '#6b7280',
                    lineHeight: '1.4'
                  }}>
                    Enable creation of duplicate lead entries in your CRM
                  </p>
                </div>
                
                {/* Toggle Switch */}
                <label style={{
                  position: 'relative',
                  display: 'inline-block',
                  width: '48px',
                  height: '26px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={isDuplicateToggle}
                    onChange={(e) => handleDuplicateToggle(e.target.checked)}
                    disabled={isLoading}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: isDuplicateToggle ? '#22c55e' : '#d1d5db',
                    transition: 'all 0.3s ease',
                    borderRadius: '26px'
                  }} />
                  <span style={{
                    position: 'absolute',
                    top: '2px',
                    left: isDuplicateToggle ? '24px' : '2px',
                    width: '22px',
                    height: '22px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.3s ease',
                    borderRadius: '50%',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }} />
                </label>
              </div>
            </div>

            {/* Quick Stats Card */}
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                margin: '0 0 16px 0', 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <BarChart3 size={18} className="text-gray-600" />
                Quick Stats
              </h3>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {leadCustomizationOptions.map((option) => {
                  const count = userPrefs[option.type]?.length || 0;
                  return (
                    <div key={option.type} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      backgroundColor: `${option.color}08`,
                      border: `1px solid ${option.color}20`,
                      borderRadius: '6px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          color: option.color
                        }}>
                          {React.cloneElement(option.icon as React.ReactElement, { size: 16 })}
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                          {option.title}
                        </span>
                      </div>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: option.color,
                        backgroundColor: `${option.color}15`,
                        padding: '2px 6px',
                        borderRadius: '4px'
                      }}>
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Help & Support Card */}
            <div style={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{ 
                margin: '0 0 12px 0', 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <HelpCircle size={18} className="text-gray-600" />
                Need Help?
              </h3>
              
              <p style={{ 
                margin: '0 0 16px 0', 
                fontSize: '13px', 
                color: '#6b7280',
                lineHeight: '1.5'
              }}>
                Learn how to customize your CRM to match your business workflow and processes.
              </p>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <button
                  onClick={() => window.open('https://help.3sigmacrm.com', '_blank')}
                  style={{
                    width: '100%',
                    backgroundColor: '#f8fafc',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#94a3b8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  📚 View Documentation
                </button>
                
                <button
                  onClick={() => window.open('https://help.3sigmacrm.com/changelog/web-app', '_blank')}
                  style={{
                    width: '100%',
                    backgroundColor: '#f8fafc',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f1f5f9';
                    e.currentTarget.style.borderColor = '#94a3b8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8fafc';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  ⚡ What's New
                </button>
              </div>
            </div>

            {/* Tips Card */}
            <div style={{
              backgroundColor: '#fefce8',
              border: '1px solid #fde047',
              borderRadius: '12px',
              padding: '16px'
            }}>
              <h4 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#a16207' 
              }}>
                💡 Pro Tip
              </h4>
              <p style={{ 
                margin: 0, 
                fontSize: '12px', 
                color: '#a16207',
                lineHeight: '1.4'
              }}>
                Changes made here will sync automatically across your mobile and web apps. 
                Test your customizations thoroughly before rolling out to your team.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomizationContainer;